import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LoadingComponent from '../../../../components/LoadingComponent';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { financialEntryApi, financialRecurrenceApi } from '../api';
import {
  detailToForm,
  formToRecurrenceCreatePayload,
  formToSplitCreatePayload,
  formToUpdatePayload,
  getMeaningfulFormItems,
} from '../mappers';
import { useFinancialFormActions } from '../FinancialFormActionsContext';
import GeneralInfoSection from './GeneralInfoSection';
import PaymentSection from './PaymentSection';
import {
  defaultFormSchemaValues,
  financialEntryFormSchema,
  FinancialEntryFormSchemaType,
} from './schema';
import { financialEntryRoutes } from '../routes';
import { FINANCIAL_ENTRY_TYPE } from '../constants';
import { FormContainer, FormFieldsScope } from './style';
import { collectValidationMessages } from './formValidation';

const FinancialEntryForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setFeedback } = useContext(FeedbackContext);
  const { setSaveHandler, setSaving } = useFinancialFormActions();
  const [loading, setLoading] = useState(!!id);

  const isEdit = Boolean(id);
  const isCreate = !id;

  const methods = useForm<FinancialEntryFormSchemaType>({
    resolver: zodResolver(financialEntryFormSchema),
    defaultValues: defaultFormSchemaValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
  } = methods;

  // O tipo é escolhido no modal aberto pelo botão "Novo lançamento".
  useEffect(() => {
    if (!isCreate) return;
    const typeParam = searchParams.get('type');
    if (typeParam === null) return;
    const type = Number(typeParam);
    if (type === FINANCIAL_ENTRY_TYPE.REVENUE || type === FINANCIAL_ENTRY_TYPE.EXPENSE) {
      setValue('type', type);
    }
  }, [isCreate, searchParams, setValue]);

  useEffect(() => {
    if (!id) return;

    const loadEntry = async () => {
      try {
        setLoading(true);
        const detail = await financialEntryApi.getById(Number(id));
        reset(detailToForm(detail));
      } catch {
        setFeedback({
          message: 'Erro ao carregar lançamento.',
          isOpen: true,
          statusOk: false,
        });
        navigate(financialEntryRoutes.list);
      } finally {
        setLoading(false);
      }
    };

    loadEntry();
  }, [id, navigate, reset, setFeedback]);

  const uploadPendingFiles = async (
    items: FinancialEntryFormSchemaType['items'],
    savedItems: Array<{ id?: number }>,
  ) => {
    const meaningfulItems = getMeaningfulFormItems(items);

    for (let i = 0; i < meaningfulItems.length; i++) {
      const pending = meaningfulItems[i].pendingFiles ?? [];
      const pendingGallery = meaningfulItems[i].pendingGalleryFiles ?? [];
      const savedItemId = meaningfulItems[i].id ?? savedItems[i]?.id;
      if (!savedItemId) continue;

      if (pending.length > 0) {
        await financialEntryApi.uploadFiles(
          savedItemId,
          pending.map((p) => p.file),
        );
      }

      if (pendingGallery.length > 0) {
        const fileIds = pendingGallery.map((g: any) => g.id).filter(Boolean);
        if (fileIds.length > 0) {
          await financialEntryApi.attachFiles(savedItemId, fileIds);
        }
      }
    }
  };

  const onSubmit = useCallback(async (values: FinancialEntryFormSchemaType) => {
    try {
      setSaving(true);

      if (isCreate && values.isRecurring) {
        const payload = formToRecurrenceCreatePayload(values);
        if (!payload) {
          setFeedback({
            message: 'Informe o valor por ocorrência e as datas da recorrência.',
            isOpen: true,
            statusOk: false,
          });
          return;
        }

        await financialRecurrenceApi.create(payload);
        setFeedback({
          message: 'Recorrência criada com sucesso.',
          isOpen: true,
          statusOk: true,
        });
        navigate(financialEntryRoutes.list);
        return;
      }

      if (isCreate) {
        const payload = formToSplitCreatePayload(values);
        const result = await financialEntryApi.create(payload);

        const primaryItems = result.items ?? [];
        await uploadPendingFiles(values.items, primaryItems);

        setFeedback({
          message: 'Lançamento criado com sucesso.',
          isOpen: true,
          statusOk: true,
        });
        navigate(financialEntryRoutes.list);
        return;
      }

      const updated = await financialEntryApi.update(
        Number(id),
        formToUpdatePayload(values),
      );

      await uploadPendingFiles(values.items, updated.items);

      setFeedback({
        message: 'Lançamento atualizado com sucesso.',
        isOpen: true,
        statusOk: true,
      });
      navigate(financialEntryRoutes.list);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        (isCreate ? 'Erro ao criar lançamento.' : 'Erro ao atualizar lançamento.');
      setFeedback({ message, isOpen: true, statusOk: false });
    } finally {
      setSaving(false);
    }
  }, [id, isCreate, navigate, setFeedback, setSaving]);

  useEffect(() => {
    setSaveHandler(() => {
      handleSubmit(onSubmit, (formErrors) => {
        const sectionMessages = collectValidationMessages(formErrors);

        setFeedback({
          message:
            sectionMessages.length > 0
              ? sectionMessages.slice(0, 3).join(' ')
              : 'Preencha os campos obrigatórios antes de salvar.',
          isOpen: true,
          statusOk: false,
        });
      })();
    });
    return () => setSaveHandler(null);
  }, [handleSubmit, onSubmit, setSaveHandler, setFeedback]);

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormFieldsScope>
          <FormContainer>
            <GeneralInfoSection />
            <PaymentSection isEdit={isEdit} />
          </FormContainer>
        </FormFieldsScope>
      </form>
    </FormProvider>
  );
};

export default FinancialEntryForm;
